import { computeModules, computePathForModule } from '@/util/modules'
import type { Analytics } from '@common/types/config/Analytics'
import type { ApiKeys } from '@common/types/config/Api'
import type { AppMode } from '@common/types/config/AppMode'
import type { ConfigSpeed } from '@common/types/config/ConfigSpeed'
import type { EntryMap } from '@common/types/config/EntryMap'
import type { Languages } from '@common/types/config/Languages'
import type { LinesGroups } from '@common/types/config/LinesGroups'
import type { MapConfig } from '@common/types/config/MapConfig'
import type { Module, ModuleConfig } from '@common/types/config/Modules'
import type { ConfigSearch } from '@common/types/config/Search'

import {
	createSelector,
	createSlice,
	type PayloadAction,
} from '@reduxjs/toolkit'

export type ConfigSlice = {
	analytics: Analytics[]
	entryMap?: EntryMap
	languages?: Languages
	modules?: ModuleConfig
	linesGroups?: LinesGroups
	apiKeys?: ApiKeys
	configSpeed?: ConfigSpeed
	search?: ConfigSearch
	mapConfig?: MapConfig
}
export const defaultConfigState: ConfigSlice = {
	analytics: [],
}

export const configSlice = createSlice({
	name: 'config',
	initialState: defaultConfigState,
	reducers: {
		setConfigAnalytics: (state, action: PayloadAction<Analytics[]>) => {
			state.analytics = action.payload
		},
		setConfigEntryMap: (state, action: PayloadAction<EntryMap>) => {
			state.entryMap = action.payload
		},
		setConfigLanguages: (state, action: PayloadAction<Languages>) => {
			state.languages = action.payload
		},
		setConfigModules: (state, action: PayloadAction<ModuleConfig>) => {
			state.modules = action.payload
		},
		setLinesGroups: (state, action: PayloadAction<LinesGroups>) => {
			state.linesGroups = action.payload
		},
		setConfigApiKeys: (state, action: PayloadAction<ApiKeys>) => {
			state.apiKeys = action.payload
		},
		setConfigSpeed: (state, action: PayloadAction<ConfigSpeed>) => {
			state.configSpeed = action.payload
		},
		setConfigSearch: (state, action: PayloadAction<ConfigSearch>) => {
			state.search = action.payload
		},
		setMapConfig: (state, action: PayloadAction<MapConfig>) => {
			state.mapConfig = action.payload
		},
	},
	selectors: {
		getConfigAnalytics: state => state.analytics,
		getConfigLanguages: state => state.languages,
		getConfigEntryMap: state => state.entryMap,
		getConfigModules: state => state.modules,
		getModules: createSelector([state => state.modules], modules => {
			if (!modules?.length) return []
			return computeModules(modules)
		}),
		getPathForModule: (state, mode: AppMode, module: Module): string => {
			const modules = state.modules
			if (!modules?.length) return '/'
			return computePathForModule(modules, mode, module)
		},
		getLinesGroups: state => state.linesGroups,
		getConfigApiKeys: state => state.apiKeys,
		getConfigSpeed: state => state.configSpeed,
		getConfigSearch: state => state.search,
		getMapConfig: state => state.mapConfig,
	},
})

export const {
	reducer: configReducer,
	actions: {
		setConfigAnalytics,
		setConfigEntryMap,
		setConfigLanguages,
		setConfigModules,
		setLinesGroups,
		setConfigApiKeys,
		setConfigSpeed,
		setConfigSearch,
		setMapConfig,
	},
	selectors: {
		getConfigAnalytics,
		getConfigEntryMap,
		getConfigLanguages,
		getConfigModules,
		getModules,
		getPathForModule,
		getLinesGroups,
		getConfigApiKeys,
		getConfigSpeed,
		getConfigSearch,
		getMapConfig,
	},
} = configSlice
